import React, { useState } from 'react'
import { FileText, Download, Eye, Search, Upload, X, AlertCircle, Loader2, Trash2, Edit } from 'lucide-react'
import { useHRDocuments, useDocumentUpload, useDocumentActions } from '@/hooks/useHRDocuments'
import { DocumentListParams, HRDocument } from '@/services/hrService'
import EditDocumentModal from '@/components/hr/EditDocumentModal'
import Pagination from '@/components/common/Pagination'
import UploadDocumentForm, { UploadFormData } from '../components/hr/UploadDocumentForm'

const HRDocumentsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [searchTerm, setSearchTerm] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [editingDocument, setEditingDocument] = useState<HRDocument | null>(null)

  // API hooks
  const queryParams: DocumentListParams = {
    category: selectedCategory === "All" ? undefined : selectedCategory,
    search: searchTerm || undefined,
    page: currentPage,
    limit: 10, // Show 10 documents per page
  }

  const { documents, loading, error, pagination, refetch } = useHRDocuments(queryParams)
  const { uploading, uploadError, uploadDocument, clearError: clearUploadError } = useDocumentUpload()
  const { loading: actionLoading, error: actionError, downloadDocument, deleteDocument, clearError: clearActionError } = useDocumentActions()

  // Get unique categories from documents
  const categories = ["All", ...Array.from(new Set(documents.map(doc => doc.category))).sort()]

  const handleFileUpload = async (uploadData: UploadFormData) => {
    const uploadParams = {
      file: uploadData.file,
      title: uploadData.title,
      description: uploadData.description,
      category: uploadData.category,
    }

    const uploadedDoc = await uploadDocument(uploadParams)
    if (uploadedDoc) {
      setShowUploadModal(false)
      refetch() // Refresh the document list
    }
  }

  const handleDownload = async (id: string, filename: string) => {
    try {
      await downloadDocument(id, filename)
    } catch (error) {
      // Error is handled by the hook
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      const success = await deleteDocument(id)
      if (success) {
        refetch() // Refresh the document list
      }
    }
  }

  const handleEdit = (document: HRDocument) => {
    setEditingDocument(document)
  }

  const handleEditSuccess = () => {
    setEditingDocument(null)
    refetch() // Refresh the document list
  }

  const handleView = async (id: string) => {
    try {
      // For now, we'll download the file. In a real app, you might want to 
      // open it in a new tab or modal viewer
      const doc = documents.find(d => d.id === id)
      if (doc) {
        await downloadDocument(id, doc.filename || doc.title)
      }
    } catch (error) {
      // Error is handled by the hook
    }
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Reset to first page when search or category changes
  const handleSearchChange = (term: string) => {
    setSearchTerm(term)
    setCurrentPage(1)
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    setCurrentPage(1)
  }

  const formatFileSize = (sizeStr: string) => {
    // If it's already formatted (contains units), return as is
    if (/[a-zA-Z]/.test(sizeStr)) return sizeStr
    
    // Otherwise, assume it's bytes and format
    const bytes = parseInt(sizeStr)
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    if (bytes === 0) return '0 Bytes'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString()
  }

  return (
    <div className="px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">HR Documents</h1>
          <p className="text-muted-foreground">
            Access important HR documents, policies, and resources
          </p>
        </div>

        {/* Error Display */}
        {(error || actionError || uploadError) && (
          <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-destructive font-medium">
                {error || actionError || uploadError}
              </span>
              <button
                onClick={() => {
                  clearUploadError()
                  clearActionError()
                }}
                className="ml-auto text-destructive hover:text-destructive/80"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              disabled={uploading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span>{uploading ? 'Uploading...' : 'Upload'}</span>
            </button>
          </div>

          <div className="flex space-x-2 overflow-x-auto">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryChange(category)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading documents...</span>
          </div>
        )}

        {/* Documents Grid */}
        {!loading && (
          <div className="bg-card border rounded-lg overflow-hidden">
            <div className="divide-y divide-border">
              {documents.map((document) => (
                <div key={document.id} className="p-6 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          <FileText className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          {document.title}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-2">
                          {document.description}
                        </p>
                        <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                          <span className="bg-muted px-2 py-1 rounded">
                            {document.category}
                          </span>
                          <span>{document.type}</span>
                          <span>{formatFileSize(document.size)}</span>
                          <span>Updated {formatDate(document.lastUpdated)}</span>
                          <span>By {document.uploadedBy}</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            document.status === 'INDEXED' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                            document.status === 'PROCESSING' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                            document.status === 'UPLOADED' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                            document.status === 'PROCESSING_FAILED' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400' :
                            document.status === 'FAILED' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {document.status === 'INDEXED' ? 'Ready' :
                             document.status === 'PROCESSING' ? 'Processing' :
                             document.status === 'UPLOADED' ? 'Queued' :
                             document.status === 'PROCESSING_FAILED' ? 'Retrying' :
                             document.status === 'FAILED' ? 'Failed' :
                             document.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleView(document.id)}
                        disabled={actionLoading}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors disabled:opacity-50"
                        title="View document"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDownload(document.id, document.filename || document.title)}
                        disabled={actionLoading}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors disabled:opacity-50"
                        title="Download document"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(document)}
                        disabled={actionLoading}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors disabled:opacity-50"
                        title="Edit document"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(document.id)}
                        disabled={actionLoading}
                        className="p-2 text-muted-foreground hover:text-destructive hover:bg-muted rounded-md transition-colors disabled:opacity-50"
                        title="Delete document"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && (
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                itemName="documents"
                showPageNumbers={true}
              />
            )}
          </div>
        )}

        {/* No Documents State */}
        {!loading && documents.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No documents found</h3>
            <p className="text-muted-foreground">
              {searchTerm ? `No documents match "${searchTerm}"` : `No documents in ${selectedCategory} category`}
            </p>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card border rounded-lg w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h3 className="text-lg font-semibold">Upload Document</h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="text-muted-foreground hover:text-foreground"
                  disabled={uploading}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              <UploadDocumentForm
                onSubmit={handleFileUpload}
                onCancel={() => setShowUploadModal(false)}
                uploading={uploading}
                error={uploadError}
                defaultCategory={selectedCategory === "All" ? "Other" : selectedCategory}
              />
            </div>
          </div>
        )}

        {/* Edit Document Modal */}
        <EditDocumentModal
          document={editingDocument}
          isOpen={!!editingDocument}
          onClose={() => setEditingDocument(null)}
          onSuccess={handleEditSuccess}
        />
      </div>
    </div>
  )
}

export default HRDocumentsPage 